import { createStore } from 'vuex';

import maxBy from 'lodash/maxBy';
import find from 'lodash/find';
import countries from '@/system/store/modules/countries'
import { OnlineBooking } from '@/online_booking/new/store/modules'

const store = createStore({
    modules: {
        countries,
        OnlineBooking
    },
    state: {
        user: null,
        selectedProducts: [],
        giftcard : null,
        discount_code: null,
        used_voucher : null,
        all_events: [],
        eventsLoadedAt: null,
        waitingListEnabled: false,
        eventTags: [],
    },

    mutations: {
        setUser(state, user) {
            state.user = user;
        },

        setGiftcard(state, giftcard) {
            state.giftcard = giftcard;
        },

        removeGiftcard(state) {
            state.giftcard = null;
        },

        setDiscountCode(state, discount_code) {
            state.discount_code = discount_code;
        },

        removeDiscountCode(state) {
            state.discount_code = null;
        },

        setUsedVoucher(state, voucher) {
            state.used_voucher= voucher;
        },

        removeUsedVoucher(state) {
            state.used_voucher = null;
        },

        clearProducts(state) {
            state.selectedProducts = [];
        },

        clearAddonProducts(state) {
            state.selectedProducts.forEach(product => {
                product.selectedAddons = [];
            });
        },

        addProduct(state, product) {
            state.selectedProducts.push(
                Object.assign({}, product, { selectedAddons: [] })
            );
        },

        addAddonProduct(state, { product, addon }) {
            let selectedProduct = find(state.selectedProducts, { id: product.id });

            if (selectedProduct) {
                selectedProduct.selectedAddons.push(addon);
            }
        },

        removeProduct(state, product) {
            let index = state.selectedProducts.findIndex(selectedProduct => (selectedProduct.id === product.id));

            state.selectedProducts.splice(index, 1);
        },

        removeAddonProduct(state, { product, addon }) {
            let selectedProduct = find(state.selectedProducts, { id: product.id });

            if (selectedProduct && selectedProduct.selectedAddons) {
                let index = selectedProduct.selectedAddons.findIndex(selectedAddon => selectedAddon.id == addon.id);

                selectedProduct.selectedAddons.splice(index, 1);
            }
        },

        toggleAddonProductSelection(state, { product, addon }) {
            let selectedProduct = find(state.selectedProducts, { id: product.id });

            if (selectedProduct) {
                let index = selectedProduct.selectedAddons.findIndex(selectedAddon => selectedAddon.id === addon.id);

                if (index > -1) {
                    selectedProduct.selectedAddons.splice(index, 1);
                } else {
                    selectedProduct.selectedAddons.push(addon);
                }
            }
        },

        removeProductAtIndex(state, index) {
            state.selectedProducts.splice(index, 1);
        },

        setAllEvents(state, events) {
            state.all_events = events;
        },

        setEventsLoadedAt(state, date = new Date()) {
            state.eventsLoadedAt = date;
        },

        setWaitingListEnabled(state, waitingListEnabled) {
            state.waitingListEnabled = waitingListEnabled;
        },

        setEventTags(state, eventTags) {
            state.eventTags = eventTags
        }
    },

    getters: {
        allEvents: (state) => {
            return state.all_events;
        },

        eventsLoadedAt: (state) => {
            return state.eventsLoadedAt;
        },

        isWaitingListEnabled: (state) => {
            return state.waitingListEnabled;
        },

        selectedProductById: (state) => (id) => {
            return find(state.selectedProducts, { id: id });
        },

        hasSelectedProducts: state => {
            return state.selectedProducts && state.selectedProducts.length > 0;
        },

        hasSelectedAddonProducts: (state, getters) => {
            return getters.selectedAddonProducts.length > 0;
        },

        hasSelectedProduct: (state) => (product) => {
            return state.selectedProducts.find(selectedProduct => selectedProduct.id === product.id) !== undefined;
        },

        hasSelectedAddonProduct: (state, getters) => ({ product, addon }) => {
            return getters.selectedProductById(product.id).selectedAddons.find(selectedAddon => selectedAddon.id === addon.id) !== undefined;
        },

        selectedProductIds: state => {
            return state.selectedProducts.map(product => {
                if (product.product) {
                    return product.product.id;
                }

                return product.id;
            });
        },

        selectedAddonProducts: state => {
            return state.selectedProducts.reduce((carry, product) => {
                if (product.selectedAddons && product.selectedAddons.length > 0) {
                    return carry.concat(product.selectedAddons);
                }

                return carry;
            }, []);
        },

        selectedAddonProductIds: (state, getters) => {
            return getters.selectedAddonProducts.map(addon => addon.id);
        },

        totalProductsDurationInMinutes: state => {
            return state.selectedProducts.reduce((carry, product) => {
                if (product.product) {
                    return carry + parseInt(product.product.time);
                }

                return carry + parseInt(product.time);
            }, 0);
        },

        totalAddonProductsDurationInMinutes: (state, getters) => {
            return getters.selectedAddonProducts.reduce((carry, addon) => {
                return carry + parseInt(addon.time);
            }, 0);
        },

        totalProductsDurationFormatted: (state, getters) => {
            return minutesToHumanReadableFormat(getters.totalProductsDurationInMinutes);
        },

        totalAddonProductsDurationFormatted: (state, getters) => {
            return minutesToHumanReadableFormat(getters.totalAddonProductsDurationInMinutes);
        },

        productCurrency: state => {
            if (state.selectedProducts[0] && state.selectedProducts[0].product) {
                return state.selectedProducts[0].product.currency;
            } else if (state.selectedProducts[0]) {
                return state.selectedProducts[0].currency;
            }

            return '';
        },

        totalProductsPrice: state => {
            return state.selectedProducts.reduce((carry, product) => {
                if (product.price > 0 && !product.clips) {
                    return carry + parseInt(product.price_in_cents_with_vat);
                } else if (product.price_including_vat_in_cents !== undefined) {
                    return carry + parseInt(product.price_including_vat_in_cents || 0);
                }

                return carry;
            }, 0) / 100;
        },

        totalProductsPriceFormatted: (state, getters) => {
            return getters.totalProductsPrice ? `${getters.productCurrency} ${getters.totalProductsPrice.toFixed(2)}` : '';
        },

        giftcardMoneyUsedForThisBooking: (state, getters) => {
            if (!state.giftcard || state.giftcard.money_left <= 0) {
                return 0;
            }

            const productsPrice = (getters.totalProductsPrice + getters.totalAddonProductsPrice - getters.discountMoneyUsedForThisBooking) * 100;

            if (productsPrice - state.giftcard.money_left >= 0) {
                return state.giftcard.money_left / 100;
            } else {
                return productsPrice / 100;
            }
        },

        giftcardMoneyUsedForThisBookingFormatted: (state, getters) => {
            return `${getters.productCurrency} ${getters.giftcardMoneyUsedForThisBooking.toFixed(2)}`;
        },

        totalProductsPriceWithGiftcard: (state, getters) => {
            const productsPrice = getters.totalProductsPrice + getters.totalAddonProductsPrice;
            const giftcardMoneyUsed = getters.giftcardMoneyUsedForThisBooking;

            return productsPrice - giftcardMoneyUsed;
        },

        totalProductsPriceWithGiftcardFormatted: (state, getters) => {
            return `${getters.productCurrency} ${getters.totalProductsPriceWithGiftcard.toFixed(2)}`;
        },

        discountMoneyUsedForThisBooking: (state, getters) => {
            if (!state.discount_code) {
                return 0;
            }

            const productsPrice = getters.totalProductsPrice + getters.totalAddonProductsPrice;

            if (state.discount_code.type == 'percentage') {
                return productsPrice * (state.discount_code.value / 100);
            } else {
                return state.discount_code.value;
            }
        },

        discountMoneyUsedForThisBookingFormatted: (state, getters) => {
          return `${getters.productCurrency} ${getters.discountMoneyUsedForThisBooking.toFixed(2)}`;
        },

        totalProductsPriceWithDiscount: (state, getters) => {
          const productsPrice = getters.totalProductsPrice + getters.totalAddonProductsPrice;
          const discountMoneyUsed = getters.discountMoneyUsedForThisBooking;

          return Math.max(0, productsPrice - discountMoneyUsed);
        },

        totalProductsPriceWithDiscountFormatted: (state, getters) => {
          return `${getters.productCurrency} ${getters.totalProductsPriceWithDiscount.toFixed(2)}`;
        },

        totalAddonProductsPrice: (state, getters) => {
            return getters.selectedAddonProducts.reduce((carry, addon) => {
                if (addon.price > 0) {
                    return carry + parseInt(addon.price_in_cents_with_vat);
                }

                return carry;
            }, 0) / 100;
        },

        totalAddonProductsPriceFormatted: (state, getters) => {
            return getters.totalAddonProductsPrice ? `${getters.productCurrency} ${getters.totalAddonProductsPrice.toFixed(2)}` : '';
        },

        totalPrice: (state, getters) => {
            return getters.totalProductsPrice + getters.totalAddonProductsPrice;
        },

        totalPriceFormatted: (state, getters) => {
            return `${getters.productCurrency} ${getters.totalPrice.toFixed(2)}`;
        },

        finalPrice: (state, getters) => {
            return getters.totalProductsPrice + getters.totalAddonProductsPrice - getters.discountMoneyUsedForThisBooking - getters.giftcardMoneyUsedForThisBooking;
        },

        finalPriceFormatted: (state, getters) => {
            return `${getters.productCurrency} ${getters.finalPrice.toFixed(2)}`;
        },

        payableProducts: state => {
            return state.selectedProducts.filter(product => {
                let p = product.product && product.client_id ? product.product : product;
                return state.user.can_receive_online_payments && (p.online_payment || p.active_in_online_booking) && p.price > 0;
            });
        },

        productPaymentOptional: (state, getters) => {
            return getters.payableProducts.filter(product => {
                return product.online_payment == 2;
            }).length == getters.payableProducts.length;
        },

        totalPriceToPayNow: (state, getters) => {
            let to_pay_now =  getters.payableProducts.reduce((carry, product) => {
                let addonsPrice = product.selectedAddons.reduce((carry, addon) => carry + parseInt(addon.price_in_cents_with_vat), 0);
                let productPrice = 0;

                if (!(product.clips || product.product)) {
                    productPrice = parseInt(product.price_in_cents_with_vat);
                } else if (product.price_including_vat_in_cents !== undefined) {
                    productPrice = parseInt(product.price_including_vat_in_cents || 0);
                }

                return carry + addonsPrice + productPrice;
            }, 0) / 100;

            return Math.max(0, (to_pay_now) - getters.discountMoneyUsedForThisBooking - getters.giftcardMoneyUsedForThisBooking);
        },

        totalPriceToPayNowFormatted: (state, getters) => {
            return getters.totalPriceToPayNow ? `${getters.productCurrency} ${getters.totalPriceToPayNow.toFixed(2)}` : '';
        },

        reservableProducts: (state, getters) => {
            return state.selectedProducts.filter(product => {
                return product.online_payment > 0 && product.online_payment_reserve;
            });
        },

        totalProductsRatePrice: (state, getters) => {
            if (!getters.hasProductsWithPaymentRates) {
                return 0;
            }

            let numberOfRates = getters.paymentRatesReferenceProduct.payment_rates_number;

            let total_rate_price = getters.productsWithPaymentRates.reduce((carry, product) => {
                let productPrice = 0;

                if (!(product.product || product.clips)) {
                    productPrice = product.price_in_cents_with_vat
                } else if (product.price_including_vat_in_cents !== undefined) {
                    productPrice = product.price_including_vat_in_cents || 0
                }

                let addonsPrice = product.selectedAddons.reduce((carry, addon) => carry + addon.price_in_cents_with_vat, 0);

                return carry + productPrice + addonsPrice;
            }, 0) / 100;

            return (total_rate_price - getters.discountMoneyUsedForThisBooking - getters.giftcardMoneyUsedForThisBooking) / numberOfRates;
        },

        totalProductsRatePriceFormatted: (state, getters) => {
            return getters.totalProductsRatePrice ? `${getters.productCurrency} ${getters.totalProductsRatePrice.toFixed(2)}` : '';
        },

        productsWithPaymentRates: state => {
            return state.selectedProducts.filter(product => {
                return product.product && product.client_id ? product.product.payment_rates : product.payment_rates;
            });
        },

        hasProductsWithPaymentRates: (state, getters) => {
            return getters.productsWithPaymentRates.length > 0;
        },

        paymentRatesReferenceProduct: (state, getters) => {
            let product = maxBy(getters.productsWithPaymentRates, (product) => {
                if (product.product && product.client_id) {
                    return product.product.price_in_cents_with_vat;
                }

                if (product.price_in_cents_with_vat !== undefined) {
                    return product.price_in_cents_with_vat || 0;
                }

                return product.price_including_vat_in_cents || 0;
            });

            return product && product.product && product.client_id ? product.product : product;
        },


        giftcard: state => {
            return state.giftcard;
        },

        discount_code: state => {
          return state.discount_code;
        },

        used_voucher: state => {
            return state.used_voucher;
        },

        selectedProductsWithAddons: (state, getters) => {
            return state.selectedProducts
                .map(product => product.product ? product.product : product)
                .filter(product => product.addons && product.addons.filter(addon => addon.available_online_book).length > 0);
        },

        hasAddonsAvailableToSelect: (state, getters) => {
            return state.user && state.user.is_addon_services_app_active && getters.selectedProductsWithAddons.length > 0;
        },

        eventTags: (state) => {
            return state.eventTags;
        },
    },
});

function minutesToHumanReadableFormat(minutesToConvert) {
    let hours = Math.floor(minutesToConvert / 60);
    let minutes = minutesToConvert % 60;
    var parts = [];

    if (hours == 1) {
        parts.push(`${hours} ${trans('online_booking.hour')}`);
    } else if (hours > 1) {
        parts.push(`${hours} ${trans('online_booking.hours')}`);
    }

    if (minutes) {
        parts.push(`${minutes} ${trans('online_booking.minutes')}`);
    }

    return parts.join(` ${trans('global.and')} `);
}

export default store;
